import { useMemo, useState, type ReactNode } from 'react'
import { useUsers } from '../hooks/useUsers'
import { useAuthSession } from '../lib/session'
import { UserManagementContext, type UserManagementContextValue } from './useUserManagementContext'

function normalizePhone(input: string) {
  return input.replace(/\D+/g, '')
}

export function UserManagementProvider({ children }: { children: ReactNode }) {
  const authSession = useAuthSession()
  const clientId = authSession?.currentClientId || ''
  const [userSearch, setUserSearch] = useState('')
  const [hideInactiveUsers, setHideInactiveUsers] = useState(false)
  const [userGroupSearch, setUserGroupSearch] = useState('')

  const usersQuery = useUsers({ clientId })

  const payload = usersQuery.data
  const allUsers = useMemo(() => payload?.rows || [], [payload?.rows])
  const groupOptions = payload?.groupOptions || []
  const reportOptions = payload?.reportOptions || []
  const bulkInviteOptions = payload?.bulkInviteUserOptions || []
  const allUserGroups = useMemo(() => payload?.userGroups || [], [payload?.userGroups])

  const users = useMemo(() => {
    let rows = [...allUsers]
    if (hideInactiveUsers) rows = rows.filter((row) => row.statusId === 1)
    const q = userSearch.trim().toLowerCase()
    const phoneQuery = normalizePhone(q)
    if (q) {
      rows = rows.filter(
        (row) =>
          row.fullName.toLowerCase().includes(q) ||
          row.email.toLowerCase().includes(q) ||
          (phoneQuery.length > 0 && normalizePhone(row.phone).includes(phoneQuery))
      )
    }
    return rows
  }, [allUsers, hideInactiveUsers, userSearch])

  const userGroups = useMemo(() => {
    const q = userGroupSearch.trim().toLowerCase()
    if (!q) return allUserGroups
    return allUserGroups.filter((row) => row.name.toLowerCase().includes(q))
  }, [allUserGroups, userGroupSearch])

  const value: UserManagementContextValue = {
    clientId,
    users,
    userGroups,
    groupOptions,
    reportOptions,
    bulkInviteOptions,
    org2FA: payload?.org2FA ?? false,
    userSearch,
    setUserSearch,
    hideInactiveUsers,
    setHideInactiveUsers,
    userGroupSearch,
    setUserGroupSearch,
    usersLoading: usersQuery.isLoading,
    usersFetching: usersQuery.isFetching,
    usersError: usersQuery.isError,
    usersErrorObject: usersQuery.error,
    groupsLoading: usersQuery.isLoading,
    groupsFetching: usersQuery.isFetching,
    groupsError: usersQuery.isError,
    groupsErrorObject: usersQuery.error,
    refetchUsers: usersQuery.refetch,
    refetchGroups: usersQuery.refetch,
  }

  return <UserManagementContext.Provider value={value}>{children}</UserManagementContext.Provider>
}
